/**
 * ADR-0222 + ADR-0224 — per-slot copy and save-error copy for the
 * business skills. Shared by Settings → Skills (`BusinessSkillsSection`)
 * and the onboarding Skills step suggestions (ADR-0226) so both surfaces
 * name and describe each skill identically. One sentence per
 * description; no exclamation points; no emoji.
 */

import type { BusinessSkillId } from '../../api/site-business-skills.js';

export const SLOT_COPY: Readonly<
  Record<BusinessSkillId, { name: string; description: string; placeholder: string }>
> = {
  get_pricing: {
    name: 'Get pricing',
    description:
      'Point agents to your pricing page. Optionally list plans so an agent can ask about one by name.',
    placeholder: 'https://example.com/pricing',
  },
  request_demo: {
    name: 'Request a demo',
    description: 'Point agents to your demo request page.',
    placeholder: 'https://example.com/demo',
  },
  sign_up: {
    name: 'Sign up',
    description:
      'Point agents to your sign-up or free-trial page. Plan-level URLs on Get pricing override this when matched.',
    placeholder: 'https://example.com/signup',
  },
  book_meeting: {
    name: 'Book a meeting',
    description:
      'Point agents to your booking link (Calendly, Cal.com, HubSpot meetings, SavvyCal, Google appointments, TidyCal, or Zcal).',
    placeholder: 'https://calendly.com/you/30min',
  },
  contact_support: {
    name: 'Contact support',
    description: 'Point agents to your support or contact page.',
    placeholder: 'https://example.com/support',
  },
  get_status: {
    name: 'Get status',
    description:
      'Point agents to your status page (statuspage.io, instatus.com, betteruptime.com, statuspal.io, status.io, or your own domain).',
    placeholder: 'https://status.example.com',
  },
  get_trust_center: {
    name: 'Get trust center',
    description:
      'Point agents to your trust, security, or compliance page (own domain, trust.*, safebase.io, trust.vanta.com, trust.drata.com, conveyor.com).',
    placeholder: 'https://trust.example.com',
  },
};

export const SAVE_REASON_COPY: Readonly<Record<string, string>> = {
  url_empty: 'URL is required to save.',
  url_too_long: 'URL is too long (max 2048 characters).',
  url_malformed: 'URL could not be parsed.',
  url_not_https: 'URL must use https://',
  url_not_a_booking_host:
    'Booking URL must be on an allowed host (Calendly, Cal.com, HubSpot meetings, SavvyCal, Google appointments, TidyCal, Zcal) or on your own domain.',
  url_not_a_status_host:
    'Status URL must be on an allowed host (statuspage.io, status.io, instatus.com, betteruptime.com, statuspal.io) or on your own domain.',
  cannot_enable_without_url: 'Enter a URL before enabling the skill.',
  plans_too_many: 'Up to 10 plans. Remove one to add another.',
  plans_name_invalid: 'Plan name is required (up to 80 characters).',
  plans_price_invalid:
    'Enter a decimal price (e.g. 19.00) or set the plan to "Contact us".',
  plans_currency_invalid: 'Currency must be a 3-letter ISO code (e.g. USD).',
  plans_period_invalid: 'Pick a billing period.',
  plans_description_invalid: 'Description is too long (max 140 characters).',
  plans_sign_up_url_url_not_https: 'Plan sign-up URL must use https://',
  plans_sign_up_url_url_malformed: 'Plan sign-up URL could not be parsed.',
  plans_not_supported: 'Plans are only valid on Get pricing.',
};
